import { compileFetchedEntity, entityBasePending } from '../shared/compile-fetched-entity';
import { encounterCreatureField, itemsCol } from '../shared/array-types';
import { refuseUnported } from '../janus-engine/unported';
import { replaceHydratedHtml } from '../shared/replace-hydrated-html';

type Row = Record<string, unknown>;
const loading = new WeakMap<HTMLElement, object>();
const reported = new WeakMap<Row, string>();
export const ENTITY_CARD_LOADING = '<div role="status" data-ft-entity-card-loading="1">Loading…</div>';

export interface EntityCardPaintHooks {
    /** Runs after an actual paint, including a deferred first paint or recovery. */
    afterPaint?: () => void;
    /** The caller's normal fallback when a compiled card fails a display gate. */
    onPaintUnavailable?: () => void;
}

/** Only failures in this loading path need reporting; cached verdicts were reported by compile. */
function reportEntityCardRefusal(row: Row, reason: string): void {
    if (entityBasePending({ _entity_refusal: reason }) || reported.get(row) === reason) return;
    reported.set(row, reason);
    refuseUnported(reason, { entityId: String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')
        ?? itemsCol(row, 'item_id') ?? ''), surface: 'entity-card' });
}

/** Synchronous card entry points claim the target now, then paint only this card after adoption. */
export function holdPendingEntityCard(row: Row, target: HTMLElement, paint: (row: Row) => boolean,
    callbacks: Pick<EntityCardPaintHooks, 'onPaintUnavailable'> & { onFailure?: () => void } = {}): boolean {
    if (!entityBasePending(row)) { loading.delete(target); return false; }
    const claim = {};
    loading.set(target, claim);
    replaceHydratedHtml(target, ENTITY_CARD_LOADING);
    // Compiler refusals already publish their own warning/event. Do not publish them again here.
    const observed = new Set<string>();
    const hear = (event: Event): void => {
        const detail = (event as CustomEvent).detail;
        if (detail?.mode === 'refused') observed.add(String(detail.reason));
    };
    window.addEventListener('ft:unported', hear);
    const current = (): boolean => loading.get(target) === claim && !!target.querySelector('[data-ft-entity-card-loading]');
    const fail = (error: unknown): void => {
        loading.delete(target);
        const message = error instanceof Error ? error.message : 'unported:entity-compile:failed';
        if (message === 'engine-version-reload') return; // The page's version banner owns this transition.
        const reason = entityBasePending({ _entity_refusal: message }) ? 'unported:entity-compile:paint-incomplete' : message;
        // A compiler verdict can carry a different telemetry code from its display text.
        if (!observed.has(reason) && row._entity_refusal !== reason && row._inventory_refusal !== reason) {
            reportEntityCardRefusal(row, reason);
        }
        const alert = document.createElement('div');
        alert.className = 'alert alert-warning'; alert.setAttribute('role', 'alert');
        alert.textContent = reason;
        replaceHydratedHtml(target, '');
        target.appendChild(alert);
        callbacks.onFailure?.();
    };
    void compileFetchedEntity(row).then(compiled => {
        if (!current()) return;
        loading.delete(target);
        try {
            if (!paint(compiled)) {
                if (callbacks.onPaintUnavailable) callbacks.onPaintUnavailable();
                else fail(new Error('unported:entity-card:paint-unavailable'));
            }
        }
        catch (error) { fail(error); }
    }).catch(error => { if (current()) fail(error); })
        .finally(() => window.removeEventListener('ft:unported', hear));
    return true;
}
